'use client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { WizardStep } from '@innovamat/glimmer-components';
import { Button, Wizard } from '@innovamat/glimmer-components';
import { useEventListener } from '@innovamat/hooks';

import { AddClassroomDropdown } from '../../components/add-classroom-dropdown';
import { AddClassroomModal } from '../../components/add-classroom-modal';

import { useSchoolCapabilities } from '../../hooks/use-school-capabilities';
import {
  JoinToClassroomProvider,
  useJoinToClassroom,
} from './join-to-classroom-context';
import {
  ConfirmClassroomsStep,
  IntroductionStep,
  SuccessClassroomsStep,
} from './steps';
import {
  FirstTimeSelectClassroomsStep,
  SelectClassroomsStep,
} from './steps/classrooms/select-classrooms-step';
import { SelectCoursesStep } from './steps/classrooms/select-courses-step';
import { ConfirmJourneysStep } from './steps/journeys/confirm-journeys-step';
import { SelectJourneysStep } from './steps/journeys/select-journeys-step';
import { SuccessJourneysStep } from './steps/journeys/success-journeys-step';

export type JoinToClassroomProps = {
  email: string;
  isFirstTime?: boolean;
  isJourney?: boolean;
  orgId: string;
  region: string;
  onGoBack: () => void;
  onGoHome: () => void;
};

const SCROLL_THRESHOLD = 50;

export const JoinToClassroom = (props: JoinToClassroomProps): JSX.Element => {
  return (
    <JoinToClassroomProvider {...props}>
      <JoinToClassroomBase
        isFirstTime={props.isFirstTime}
        isJourney={props.isJourney}
      />
    </JoinToClassroomProvider>
  );
};

function JoinToClassroomBase({
  isFirstTime = false,
  isJourney = false,
}: {
  isFirstTime?: boolean;
  isJourney?: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const {
    step,
    editModalData,
    goToNextStep,
    goToPreviousStep,
    handleSuccessNewClassroom,
    selectedCourses,
    handleConfirm,
    assignMutationIsPending,
    selectedClassrooms,
    onGoHome,
    orgHasClassrooms,
  } = useJoinToClassroom();

  const [showAddClassroomDropdown, setShowAddClassroomDropdown] =
    useState(false);

  const {
    classrooms: { canCreateClassrooms },
  } = useSchoolCapabilities();

  const checkScroll = (): void => {
    setShowAddClassroomDropdown(window.scrollY > SCROLL_THRESHOLD);
  };

  useEventListener('scroll', checkScroll);

  const getFirstTimeSteps = () => {
    const steps: WizardStep[] = [
      {
        title: t('classrooms.steps.firstStep.title'),
        description: t('classrooms.steps.firstStep.subtitle'),
        content: <IntroductionStep />,
      },
    ];

    if (orgHasClassrooms) {
      steps.push({
        title: t('classrooms.select-classrooms.select-course'),
        description: t(
          'classrooms.select-classrooms.select-course.description'
        ),
        content: <SelectCoursesStep />,
        isNextStepDisabled: selectedCourses.length === 0,
        rightFooterActions: (
          <Button
            onClick={goToNextStep}
            disabled={selectedCourses.length === 0}
          >
            {t('common.continue')}
          </Button>
        ),
      });
    }

    steps.push({
      title: t('classrooms.select-classrooms.select-classroom'),
      description: orgHasClassrooms
        ? t('classrooms.select-classrooms.select-classroom.description')
        : undefined,
      titleRightActions: (
        <AddClassroomDropdown
          options={canCreateClassrooms ? ['add', 'import'] : []}
          size="M"
          onSuccess={handleSuccessNewClassroom}
        />
      ),
      content: <FirstTimeSelectClassroomsStep />,
      leftFooterActions: showAddClassroomDropdown && (
        <AddClassroomDropdown
          options={canCreateClassrooms ? ['add', 'import'] : []}
          position="topLeft"
          size="M"
          onSuccess={handleSuccessNewClassroom}
        />
      ),
      isNextStepDisabled: selectedClassrooms.length === 0,
      rightFooterActions: (
        <Button
          onClick={goToNextStep}
          disabled={selectedClassrooms.length === 0}
        >
          {t('common.continue')}
        </Button>
      ),
    });

    steps.push({
      title: t('classrooms.select-classrooms.confirm.title'),
      content: <ConfirmClassroomsStep />,
      rightFooterActions: (
        <>
          <Button onClick={goToPreviousStep} variant="secondary">
            {t('join-classroom.footer.edit')}
          </Button>

          <Button onClick={handleConfirm} loading={assignMutationIsPending}>
            {t('join-classroom.footer.confirm')}
          </Button>
        </>
      ),
    });

    steps.push({
      title: t('classrooms.steps.success.title'),
      content: <SuccessClassroomsStep />,
      hideBackButton: true,
      rightFooterActions: (
        <Button onClick={onGoHome}>{t('join-classroom.footer.go-home')}</Button>
      ),
    });

    return steps;
  };

  const getJoinToClassroomSteps = () => [
    {
      title: t('classrooms.select-classrooms.join-classroom'),
      description: t('classrooms.select-classrooms.join-classroom.description'),
      onGoBack: onGoHome,
      content: <SelectClassroomsStep />,
      isNextStepDisabled: selectedClassrooms.length === 0,
      rightFooterActions: (
        <Button
          onClick={goToNextStep}
          disabled={selectedClassrooms.length === 0}
        >
          {t('common.continue')}
        </Button>
      ),
    },
    {
      title: t('classrooms.select-classrooms.confirm.title'),
      content: <ConfirmClassroomsStep />,
      rightFooterActions: (
        <>
          <Button onClick={goToPreviousStep} variant="secondary">
            {t('join-classroom.footer.edit')}
          </Button>

          <Button onClick={handleConfirm} loading={assignMutationIsPending}>
            {t('join-classroom.footer.confirm')}
          </Button>
        </>
      ),
    },
    {
      title: t('classrooms.steps.success.title'),
      content: <SuccessClassroomsStep />,
      hideBackButton: true,
      rightFooterActions: (
        <Button onClick={onGoHome}>{t('join-classroom.footer.go-home')}</Button>
      ),
    },
  ];

  const getJoinToJourneySteps = () => {
    const steps: WizardStep[] = [];

    steps.push({
      title: t('classrooms.select-journeys.select-groups.title'),
      description: orgHasClassrooms
        ? t('classrooms.select-journeys.select-groups.description')
        : undefined,
      titleRightActions: (
        <AddClassroomDropdown
          options={['add-journey']}
          size="M"
          onSuccess={handleSuccessNewClassroom}
        />
      ),
      onGoBack: onGoHome,
      content: <SelectJourneysStep />,
      isNextStepDisabled: selectedClassrooms.length === 0,
      rightFooterActions: (
        <Button
          onClick={goToNextStep}
          disabled={selectedClassrooms.length === 0}
        >
          {t('common.continue')}
        </Button>
      ),
    });

    steps.push({
      title: t('classrooms.steps.confirm-journeys.title'),
      content: <ConfirmJourneysStep />,
      rightFooterActions: (
        <>
          <Button onClick={goToPreviousStep} variant="secondary">
            {t('join-classroom.footer.edit')}
          </Button>

          <Button onClick={handleConfirm} loading={assignMutationIsPending}>
            {t('join-classroom.footer.confirm')}
          </Button>
        </>
      ),
    });

    steps.push({
      title: t('classrooms.steps.success.title'),
      content: <SuccessJourneysStep />,
      hideBackButton: true,
      rightFooterActions: (
        <Button onClick={onGoHome}>{t('join-classroom.footer.go-home')}</Button>
      ),
    });

    return steps;
  };

  const steps = isJourney
    ? getJoinToJourneySteps()
    : isFirstTime
    ? getFirstTimeSteps()
    : getJoinToClassroomSteps();

  return (
    <>
      <Wizard
        steps={steps}
        currentStep={step}
        handleNextStep={goToNextStep}
        handlePrevStep={goToPreviousStep}
      />
      <AddClassroomModal {...editModalData} isCourseSelectorAutofilled />
    </>
  );
}
